import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  LinearProgress,
  Alert,
} from "@mui/material";
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";

const AssignedAssetCard = ({ asset, onClick }) => {
  const getPriorityColor = (priority) => {
    switch (priority.toLowerCase()) {
      case "high":
        return { backgroundColor: "#FFEBEE", color: "#C62828" };
      case "moderate":
        return { backgroundColor: "#FFF3E0", color: "#E65100" };
      case "low":
        return { backgroundColor: "#E8F5E9", color: "#2E7D32" };
      default:
        return { backgroundColor: "#F5F5F5", color: "#757575" };
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "online":
        return { backgroundColor: "#E8F5E9", color: "#2E7D32" };
      case "offline":
        return { backgroundColor: "#EEEEEE", color: "#616161" };
      default:
        return { backgroundColor: "#F5F5F5", color: "#757575" };
    }
  };

  const getAlertSeverity = (type) => {
    switch (type) {
      case "error":
        return "error";
      case "warning":
        return "warning";
      default:
        return "info";
    }
  };

  const getHealthScoreColor = (score) => {
    if (score >= 70) return "#399432";
    if (score >= 60) return "#FFC107";
    return "#F44336";
  };

  const isSensorAbnormal = (value, normal) => {
    const numValue = parseFloat(value);
    const [min, max] = normal.split("-").map(parseFloat);
    return numValue < min || numValue > max;
  };

  const getBorderColor = (alertType) => {
    switch (alertType) {
      case "error":
        return "#F44336";
      case "warning":
        return "#FF9800";
      default:
        return "#E0E0E0";
    }
  };

  return (
    <Card
      onClick={onClick}
      sx={{
        cursor: "pointer",
        transition: "all 0.2s ease",
        border: "1px solid #E0E0E0",
        boxShadow: "none",
        borderRadius: "12px",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        "&:hover": {
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          transform: "translateY(-2px)",
          border: `2px solid ${getBorderColor(asset.alert.type)}`,
        },
      }}
    >
      <CardContent sx={{ p: 3, flexGrow: 1, display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <Box sx={{ mb: 2, position: "relative" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, position: "absolute", top: 0, right: 0 }}>
            <Chip
              label={asset.priority}
              size="small"
              sx={{
                ...getPriorityColor(asset.priority),
                fontWeight: 600,
                fontSize: "0.75rem",
                height: "22px",
              }}
            />
            <Chip
              label={asset.status}
              size="small"
              sx={{
                ...getStatusColor(asset.status),
                fontWeight: 600,
                fontSize: "0.75rem",
                height: "22px",
              }}
            />
          </Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, fontSize: "1.125rem", pr: 16, mb: 0.5 }}
          >
            {asset.workOrderId ? `${asset.workOrderId} - ${asset.name}` : asset.name}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "#757575", fontSize: "0.875rem" }}
          >
            {asset.location.workspace} › {asset.location.zone} ›{" "}
            {asset.location.area}
          </Typography>
        </Box>

        {/* Health Score */}
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: "#424242" }}
            >
              Asset Health Score
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, fontSize: "1.5rem" }}
            >
              {asset.healthScore}/100
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={asset.healthScore}
            sx={{
              height: 8,
              borderRadius: 1,
              backgroundColor: "#E0E0E0",
              "& .MuiLinearProgress-bar": {
                backgroundColor: getHealthScoreColor(asset.healthScore),
                borderRadius: 1,
              },
            }}
          />
        </Box>

        {/* Alert */}
        <Alert
          severity={getAlertSeverity(asset.alert.type)}
          icon={asset.alert.type === "error" ? <ErrorIcon /> : <WarningIcon />}
          sx={{
            mb: 2,
            alignItems: "center",
            borderRadius: "8px",
            "& .MuiAlert-message": {
              width: "100%",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {asset.alert.message}
            </Typography>
            {/* <Chip
              label={asset.alert.confidence}
              size="small"
              sx={{
                backgroundColor:
                  asset.alert.type === "error" ? "#FFCDD2" : "#FFE0B2",
                color: asset.alert.type === "error" ? "#C62828" : "#E65100",
                fontWeight: 600,
                fontSize: "0.75rem",
                height: "20px",
              }}
            /> */}
          </Box>
        </Alert>

        {/* Metrics */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
            },
            gap: 2,
            mb: 2,
          }}
        >
          <Box>
            <Typography
              variant="caption"
              sx={{ color: "#757575", fontSize: "0.75rem" }}
            >
              {asset.rul.label}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
              }}
            >
              {asset.rul.value}
            </Typography>
          </Box>
          {/* <Box>
            <Typography
              variant="caption"
              sx={{ color: "#757575", fontSize: "0.75rem" }}
            >
              {asset.workOrders.label}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {asset.workOrders.value}
            </Typography>
          </Box> */}
          <Box>
            <Typography
              variant="caption"
              sx={{ color: "#757575"}}
            >
              {asset.nextService.label}
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontWeight: 600 }}
            >
              {asset.nextService.value}
            </Typography>
          </Box>
        </Box>

        {/* Sensors - Using CSS Grid for exact 1/3 width */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
            },
            gap: 2,
            mt: "auto",
          }}
        >
          {Object.entries(asset.sensors).map(([key, sensor]) => {
            const isAbnormal = isSensorAbnormal(sensor.value, sensor.normal);
            return (
              <Box
                key={key}
                sx={{
                  textAlign: "center",
                  p: 1.5,
                  border: "1px solid #E0E0E0",
                  borderRadius: "8px",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "#757575",
                    fontSize: "0.75rem",
                    display: "block",
                    mb: 0.5,
                  }}
                >
                  {sensor.label}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 700,
                    fontSize: "1.125rem",
                    color: isAbnormal ? "#F57C00" : "#212121",
                    mb: 0.5,
                  }}
                >
                  {sensor.value}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "#9E9E9E", fontSize: "0.7rem" }}
                >
                  Normal: {sensor.normal}
                </Typography>

              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
};

export default AssignedAssetCard;
